import HeaderComponent from '@/components/HeaderRefactored/HeaderRefactored';
import { ListGold } from '@/resources/assets/images';
import { Pagination } from '@/components/atoms/Pagination';
import { usePagination } from '@/hooks/usePagination';
import type { RootState } from '@/rootReducer';
import { sendAnalytics } from '@/services/analyticsService';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { LIST_LIMIT } from '../constants/ListLimit';
import { useListsData } from '../hooks/useListsData';
import { ListHeader } from '../components/atoms/ListHeader';
import { ListsTable } from '../components/atoms/ListTable';
import { useResetQueryMutation } from '../hooks/useResetQueryMutation';

export type SortByProps = {
	key: 'name' | 'businessIds';
	order: 'asc' | 'desc' | 'neutral';
};

export const ListsManagementPage = () => {
	const { mutate: mutateResetQuery } = useResetQueryMutation();

	useEffect(() => {
		mutateResetQuery();
	}, [mutateResetQuery]);

	const { lists } = useListsData();

	const [skip, setSkip] = useState(0);
	const [search, setSearch] = useState('');
	const [sortBy, setSortBy] = useState<SortByProps | null>(null);
	const user = useSelector((state: RootState) =>
		state.user.getIn(['authorize', 'user']).toJS()
	);
	const { page, totalPages, onGotoPage, onNextPage, onPreviousPage } =
		usePagination({
			skip,
			count: lists.length,
			limitPerPage: LIST_LIMIT,
			onPageChange: setSkip
		});

	const currentPageData = useMemo(() => {
		const searchableList = search
			? lists.filter(({ name }) =>
					name
						.toLocaleLowerCase()
						.trim()
						.includes(search.toLocaleLowerCase().trim())
				)
			: lists;

		const initialIndex = (page - 1) * LIST_LIMIT;
		const finalIndex = page * LIST_LIMIT;

		const sortedList = searchableList.sort((listA, listB) => {
			if (!sortBy || sortBy?.order === 'neutral') {
				const dateA = new Date(listA.updatedAt).getTime();
				const dateB = new Date(listB.updatedAt).getTime();
				return dateB - dateA;
			}

			if (sortBy.key === 'businessIds') {
				return sortBy.order === 'asc'
					? listA.businessIds.length - listB.businessIds.length
					: listB.businessIds.length - listA.businessIds.length;
			}

			if (sortBy.key === 'name') {
				return sortBy.order === 'asc'
					? listA.name.localeCompare(listB.name) -
							listB.name.localeCompare(listB.name)
					: listB.name.localeCompare(listB.name) -
							listA.name.localeCompare(listB.name);
			}

			return 0;
		});

		const slicedList = sortedList.slice(initialIndex, finalIndex);

		return slicedList;
	}, [lists, page, search, sortBy]);

	const isSorted = sortBy && sortBy.order !== 'neutral';

	const handleFilterByName = (value: string) => {
		setSearch(value);
	};

	const handleSortByKey = (key: SortByProps['key']) => {
		setSortBy(state => {
			if (key === state?.key) {
				if (state.order === 'asc') {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						properties: {
							sort: key,
							direction: 'desc'
						},
						eventName: 'User sorted list of lists'
					});
					return { ...state, order: 'desc' };
				}

				if (state.order === 'desc') {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						properties: {
							sort: key,
							direction: 'neutral'
						},
						eventName: 'User sorted list of lists'
					});
					return { ...state, order: 'neutral' };
				}
				sendAnalytics({
					type: 'track',
					userId: user.email,
					properties: {
						sort: key,
						direction: 'asc'
					},
					eventName: 'User sorted list of lists'
				});

				return { ...state, order: 'asc' };
			}

			sendAnalytics({
				type: 'track',
				userId: user.email,
				properties: {
					sort: key,
					direction: 'asc'
				},
				eventName: 'User sorted list of lists'
			});

			return { key, order: 'asc' };
		});
	};

	return (
		<div className="flex h-full w-full flex-col gap-4 overflow-auto p-3">
			<HeaderComponent
				icon={<ListGold />}
				name="Manage Lists"
				description="Keep your prospecting organized and filter businesses by your needs using lists"
			/>

			<div className="flex h-full flex-col gap-4 rounded-md bg-dm-black p-4 lg:p-9">
				<ListHeader
					currentPage={page}
					totalElements={lists.length}
					onFilterByName={handleFilterByName}
					onSortByChange={handleSortByKey}
					sortByKey={isSorted ? sortBy?.key : null}
				/>

				<ListsTable
					listData={lists}
					pageListData={currentPageData}
					currentPage={page}
					sorting={
						isSorted
							? [
									{
										id: sortBy.key,
										desc: sortBy.order === 'desc'
									}
								]
							: []
					}
				/>

				{totalPages > 1 && (
					<Pagination
						currentPage={page}
						totalPages={totalPages}
						onNextPage={onNextPage}
						onPreviousPage={onPreviousPage}
						onGotoPage={onGotoPage}
					/>
				)}
			</div>
		</div>
	);
};
